import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Pin, Sparkles } from "lucide-react";
import { ASSETS, SITE_ORIGIN } from "../lib/constants";
import Footer from "../components/Footer";
import useBreadcrumbJsonLd from "../lib/useBreadcrumbJsonLd";
import { BLOG_POSTS } from "../data/blog";

function useIndexMeta() {
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" in window ? "instant" : "auto" });
        document.title = "Blog · TRT Plus";
        const set = (selector, attr, value) => {
            const el = document.querySelector(selector);
            if (el) el.setAttribute(attr, value);
        };
        const desc =
            "Long-form articles on hormone optimization, TRT protocols, bloodwork analysis, and the models behind TRT Plus.";
        set('meta[name="description"]', "content", desc);
        set('meta[property="og:title"]', "content", "Blog · TRT Plus");
        set('meta[property="og:description"]', "content", desc);
        set('meta[property="og:type"]', "content", "website");
        set('meta[property="og:url"]', "content", `${SITE_ORIGIN}/blog`);
        set('meta[name="twitter:title"]', "content", "Blog · TRT Plus");
        set('meta[name="twitter:description"]', "content", desc);
        set('link[rel="canonical"]', "href", `${SITE_ORIGIN}/blog`);
    }, []);
}

export default function BlogIndex() {
    useIndexMeta();
    useBreadcrumbJsonLd([
        { name: "Home", url: `${SITE_ORIGIN}/` },
        { name: "Blog", url: `${SITE_ORIGIN}/blog` },
    ]);

    // Sort: pinned posts first (by `pinned: true` meta flag), then
    // everything else by isoDate descending (newest first).
    const sortedPosts = useMemo(() => {
        const toTime = (p) =>
            new Date(p.isoDate || p.isoModified || 0).getTime();
        return [...BLOG_POSTS].sort((a, b) => {
            if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1;
            return toTime(b) - toTime(a);
        });
    }, []);

    return (
        <main
            data-testid="page-blog-index"
            className="relative bg-black text-[#f4f5f7] font-body min-h-screen"
        >
            {/* slim header */}
            <header className="sticky top-0 z-40 bg-black/80 backdrop-blur-xl border-b border-white/5">
                <div className="max-w-4xl mx-auto px-6 lg:px-10 flex items-center justify-between h-16">
                    <Link
                        to="/"
                        data-testid="blog-index-home-link"
                        className="flex items-center gap-2.5 text-white"
                    >
                        <img
                            src={ASSETS.icon}
                            alt="TRT Plus"
                            className="h-8 w-8 rounded-md"
                        />
                        <span className="font-brand text-2xl leading-none">
                            <span className="text-white">TRT</span>
                            <span className="text-[#2ba6e0]">+</span>
                        </span>
                    </Link>
                    <Link
                        to="/"
                        data-testid="blog-index-back-link"
                        className="inline-flex items-center gap-2 text-sm text-white/65 hover:text-white transition-colors"
                    >
                        <ArrowLeft size={16} /> Back to home
                    </Link>
                </div>
            </header>

            {/* hero */}
            <section className="relative pt-20 lg:pt-28 pb-12 lg:pb-16 overflow-hidden border-b border-white/5">
                <div className="absolute inset-0 grid-backdrop opacity-30 pointer-events-none" />
                <div className="absolute inset-0 radial-glow opacity-60 pointer-events-none" />
                <div className="relative max-w-4xl mx-auto px-6 lg:px-10">
                    <div className="eyebrow mb-5">
                        <span className="dot" /> Blog
                    </div>
                    <h1
                        className="font-display font-bold text-white leading-tight"
                        style={{
                            fontSize: "clamp(2.25rem, 5vw, 3.75rem)",
                            letterSpacing: "-0.02em",
                        }}
                        data-testid="blog-index-title"
                    >
                        Clinical precision,
                        <br />
                        <span className="blue-glow">long-form.</span>
                    </h1>
                    <p className="mt-5 max-w-2xl text-white/65 text-base lg:text-lg leading-relaxed">
                        Deep-dive articles on TRT protocols, bloodwork analysis,
                        pharmacokinetics, and the engineering behind TRT Plus.
                    </p>
                </div>
            </section>

            {/* posts */}
            <section className="relative py-16 lg:py-20">
                <div className="max-w-3xl mx-auto px-6 lg:px-10">
                    <ul className="space-y-8" data-testid="blog-posts-list">
                        {sortedPosts.map((post) => (
                            <li key={post.slug}>
                                <Link
                                    to={`/blog/${post.slug}`}
                                    data-testid={`blog-post-${post.slug}`}
                                    className="tile block p-6 lg:p-8 hover:border-white/20 transition-colors"
                                >
                                    <div className="flex items-center flex-wrap gap-2 mb-3">
                                        <div className="text-[11px] uppercase tracking-widest font-mono text-[#49bef5]">
                                            {post.category}
                                        </div>
                                        {post.showStartHereChip && (
                                            <span
                                                data-testid={`chip-start-here-${post.slug}`}
                                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#49bef5]/40 bg-[#49bef5]/[0.08] text-[9.5px] uppercase tracking-[0.14em] font-mono text-[#49bef5]"
                                            >
                                                <Pin size={9} strokeWidth={2.4} />
                                                Start here
                                            </span>
                                        )}
                                        {post.showLatestChip && (
                                            <span
                                                data-testid={`chip-latest-${post.slug}`}
                                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#49bef5]/40 bg-[#49bef5]/[0.08] text-[9.5px] uppercase tracking-[0.14em] font-mono text-[#49bef5]"
                                            >
                                                <Sparkles size={9} strokeWidth={2.4} />
                                                Latest
                                            </span>
                                        )}
                                    </div>
                                    <h2 className="font-display font-bold text-white text-2xl lg:text-3xl leading-tight mb-3">
                                        {post.title}
                                    </h2>
                                    <p className="text-white/65 text-[15px] leading-relaxed mb-4">
                                        {post.description}
                                    </p>
                                    <div className="text-xs font-mono text-white/40 uppercase tracking-widest">
                                        {post.date} · {post.readingTime}
                                    </div>
                                </Link>
                            </li>
                        ))}
                    </ul>

                    {sortedPosts.length === 0 && (
                        <p className="text-white/50">
                            No articles yet — check back soon.
                        </p>
                    )}
                </div>
            </section>

            <Footer />
        </main>
    );
}
